import { useEffect, useState, useCallback } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

// Admin-overzicht: welke app-versie draait elk (recent) ingelogd apparaat/gebruiker?
// Bouwt voort op /presence (elk apparaat meldt zijn versie mee bij de ping).
export function VersieOverzicht({ api, currentVersion, currentBuild }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/presence/versions");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [api]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("nl-NL"); } catch (e) { return String(iso); } };
  const online = (iso) => { if (!iso) return false; return (Date.now() - new Date(iso).getTime()) < 90000; };
  // "actueel" = meldt de huidige versie; "oud" = meldt een andere versie, OF is nu online maar meldt geen versie
  // (dan draait dat apparaat nog een oude build die de versie niet doorgeeft); anders "onbekend" (al langer niet actief).
  const statusOf = (r) => {
    if (r.version && currentVersion) return r.version === currentVersion ? "actueel" : "oud";
    return online(r.last_seen) ? "oud" : "onbekend";
  };
  const outdated = rows.filter((r) => statusOf(r) === "oud").length;

  return (
    <div data-testid="versies-tab" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">App-versies per gebruiker</h1>
          <p className="text-sm text-slate-500">Zie welke versie elk apparaat draait, zodat je kunt controleren of iedereen op dezelfde (nieuwste) versie werkt.</p>
        </div>
        <button data-testid="versies-refresh" onClick={load} disabled={busy} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />Vernieuwen
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Nieuwste versie</div>
          <div className="mt-1 text-2xl font-bold text-[#1C64B0]" data-testid="versies-current">{currentVersion}</div>
          <div className="text-xs text-slate-400">{currentBuild}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Recente apparaten</div>
          <div className="mt-1 text-2xl font-bold text-slate-800">{rows.length}</div>
        </div>
        <div className={`rounded-xl border p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] ${outdated ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Op oude versie</div>
          <div className={`mt-1 text-2xl font-bold ${outdated ? "text-amber-700" : "text-emerald-700"}`} data-testid="versies-outdated">{outdated}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200">{["Gebruiker", "Rol", "Versie", "Build", "Status", "Laatst gezien"].map((c, i) => <th key={i} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{c}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="text-center text-slate-400 py-8">{busy ? "Laden…" : "Nog geen apparaten gezien."}</td></tr> : rows.map((r, i) => {
              const st = statusOf(r); const isOld = st === "oud";
              return (
                <tr key={i} data-testid="versies-row" className={`border-t border-slate-100 ${isOld ? "bg-amber-50/60" : "hover:bg-slate-50/80"}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-700 whitespace-nowrap">{r.name || r.email || "—"}{online(r.last_seen) && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" title="Nu online" />}</td>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{r.role || "—"}</td>
                  <td className="px-4 py-2.5 font-mono whitespace-nowrap">{r.version || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500 whitespace-nowrap">{r.build || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{st === "actueel" ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="h-4 w-4" />Actueel</span> : st === "oud" ? <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" title={r.version ? "" : "Nu online maar meldt geen versie — draait nog een oude versie"}><AlertTriangle className="h-4 w-4" />Oude versie{!r.version && <span className="font-normal text-amber-600"> (niet vernieuwd)</span>}</span> : <span className="text-slate-400">Onbekend</span>}</td>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmt(r.last_seen)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">Apparaten op een oude versie krijgen automatisch een <b>verplicht volledig-scherm venster</b> te zien ("Nieuwe versie vereist") en worden binnen 10 seconden automatisch vernieuwd — ze kunnen niet verder werken op de oude versie. <b>Onbekend</b> = het apparaat heeft nog geen versie gemeld (nog niet geopend sinds de update, of al langer niet actief) — zodra ze de app openen verschijnt hun versie hier.</p>
    </div>
  );
}
